/**
 * Where the tier pill actually sits on the track, so the "You" marker can be placed under its
 * CENTRE instead of under the raw value.
 *
 * The pill and the marker state the same thing — the tier you are on — so they read as one unit
 * only when they line up. They cannot be lined up in CSS alone: the pill is sized by its label
 * (px) while every position on the bar is a percentage, so the offset between the value and the
 * pill's centre is only known once both are measured. Same approach the configurator's find-my-pack
 * bar takes for its marker labels (`detail-marker.ts`): keep the geometry as plain functions, feed
 * them real measurements, and unit-test them without rendering.
 */

/**
 * The pill's centre as a percentage of the track — where the "You" marker is anchored.
 *
 * The pill's right edge normally sits at the fill's tip. When the label is wider than the fill, the
 * slot grows rightwards instead of squeezing the label (see `PillSlot`), so the right edge is the
 * label's own width — `Math.max` covers both.
 *
 * `null` when nothing is measured yet (first paint, or no pill), which leaves the marker on the raw
 * value until a frame has been laid out.
 */
/**
 * How far a marker's label has to slide, in px, to stay on the track while its arrow stays put.
 *
 * The arrow marks a point and the label names it, so the label is centred under the arrow — but
 * near the track's ends a wide label ("You and Market" is 78px on a 180px card) cannot be both
 * centred and inside. Something has to give, and it is the label: it slides, the arrow does not,
 * because the arrow is the part that carries the meaning. Positive slides right (off the left
 * edge), negative slides left (off the right edge), 0 when it already fits — the same rule the
 * configurator's find-my-pack bar applies to its own marker labels (`detail-marker.ts`).
 */
export const labelClampPx = (arrowPx: number, labelPx: number, trackPx: number): number => {
  if (!Number.isFinite(labelPx) || !Number.isFinite(trackPx) || labelPx <= 0 || trackPx <= 0) {
    return 0;
  }
  const half = labelPx / 2;
  // A label wider than the track pokes out BOTH ends, and correcting the right one would drag its
  // left edge further off; pin the left edge instead, so the text starts where it can be read.
  if (labelPx >= trackPx) return half - arrowPx;
  const overLeft = Math.max(0, half - arrowPx);
  const overRight = Math.max(0, arrowPx + half - trackPx);
  // Returned as a plain 0 when neither edge is crossed — `-0` is a different value to `Object.is`,
  // and callers compare.
  if (overLeft > 0) return overLeft;
  return overRight > 0 ? -overRight : 0;
};

export const pillCentrePct = (tipPct: number, pillPx: number, trackPx: number): number | null => {
  if (!Number.isFinite(pillPx) || !Number.isFinite(trackPx) || pillPx <= 0 || trackPx <= 0) {
    return null;
  }
  const tipPx = (Math.min(100, Math.max(0, tipPct)) / 100) * trackPx;
  const rightPx = Math.max(tipPx, pillPx);
  return ((rightPx - pillPx / 2) / trackPx) * 100;
};
